import React, { useState, useEffect } from "react";
import { getChangeRequests, reviewChangeRequest } from "../../services/platformApi";
import "./AdminOnboarding.css";

/**
 * ChangeRequests — admin reviews employee profile-edit requests.
 * Redesigned (crx-* classes) to match the Onboarding dashboard style.
 * Shows OLD -> NEW for each field, previews re-uploaded documents,
 * and lets the admin Approve or Reject (with a note).
 */

const LABELS = {
  personalEmail: "Personal Email", phone: "Phone", bloodGroup: "Blood Group",
  currentAddress: "Current Address", currentCity: "Current City",
  currentState: "Current State", currentPincode: "Current Pincode",
  permanentAddress: "Permanent Address", permanentCity: "Permanent City",
  permanentState: "Permanent State", permanentPincode: "Permanent Pincode",
  emergencyContactName: "Emergency Contact", emergencyContactPhone: "Emergency Phone",
  aadharDocKey: "Aadhar Card", panDocKey: "PAN Card", photoKey: "Passport Photo",
};

/* Inline SVG icons — no emoji, no icon library */
const Ico = {
  edit: <svg viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4z" /></svg>,
  search: <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>,
  refresh: <svg viewBox="0 0 24 24"><path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.5 9a9 9 0 0114.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0020.5 15" /></svg>,
  arrow: <svg viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7" /></svg>,
  check: <svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>,
  x: <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>,
  doc: <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" /></svg>,
  inbox: <svg viewBox="0 0 24 24"><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" /></svg>,
  warn: <svg viewBox="0 0 24 24"><path d="M10.3 3.9l-8 14A2 2 0 004 21h16a2 2 0 001.7-3.1l-8-14a2 2 0 00-3.4 0z" /><path d="M12 9v4M12 17h.01" /></svg>,
};

const AV_CLASSES = ["aod-av0", "aod-av1", "aod-av2", "aod-av3", "aod-av4"];
const avClass = (seed) => {
  const s = String(seed || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AV_CLASSES[h % AV_CLASSES.length];
};
const initials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

function ChangeRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState({ open: false, title: "", url: "", isPdf: false });

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getChangeRequests();
      setRequests(res.requests || []);
    } catch (err) {
      setError(err.message || "Failed to load change requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const act = async (id, decision) => {
    let note = "";
    if (decision === "REJECT") {
      note = window.prompt("Reason for rejection (optional):") || "";
    }
    setBusyId(id);
    try {
      await reviewChangeRequest(id, decision, note);
      await load();
    } catch (err) {
      setError(err.message || "Action failed");
    } finally {
      setBusyId("");
    }
  };

  const openPreview = (title, url) => {
    if (!url) return;
    setPreview({ open: true, title, url, isPdf: /\.pdf(\?|$)/i.test(url) || /pdf/i.test(url) });
  };

  const closePreview = () => setPreview({ open: false, title: "", url: "", isPdf: false });

  // Search filter (name / email / empId)
  const q = search.trim().toLowerCase();
  const filtered = requests.filter((r) => {
    if (!q) return true;
    return (
      String(r.fullName || "").toLowerCase().includes(q) ||
      String(r.email || "").toLowerCase().includes(q) ||
      String(r.employeeId || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="aod">
      {/* Premium header band */}
      <div className="aod-band">
        <div className="aod-band__row">
          <div className="aod-brand">
            <div className="aod-brand__logo">{Ico.edit}</div>
            <div>
              <h1>Change Requests</h1>
              <p>Review &amp; approve employee profile edit requests</p>
            </div>
          </div>
          <div className="aod-band__r">
            <div className="aod-gsearch">
              {Ico.search}
              <input
                type="text"
                placeholder="Search name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && <button className="aod-gsearch__clear" onClick={() => setSearch("")} title="Clear">×</button>}
            </div>
            <button className="aod-gbtn" onClick={load} disabled={loading} title="Refresh">{Ico.refresh}</button>
          </div>
        </div>
      </div>

      <div className="aod-wrap">
        {/* Stats */}
        <div className="aod-stats">
          <div className="aod-card">
            <div className="aod-card__top"><div className="aod-card__ic aod-ic-amber">{Ico.edit}</div></div>
            <div className="aod-card__num">{requests.length}</div>
            <div className="aod-card__lbl">Pending Changes</div>
          </div>
          <div className="aod-card">
            <div className="aod-card__top"><div className="aod-card__ic aod-ic-navy">{Ico.doc}</div></div>
            <div className="aod-card__num">
              {requests.reduce((n, r) => n + (r.documentPreviews ? Object.keys(r.documentPreviews).length : 0), 0)}
            </div>
            <div className="aod-card__lbl">Documents to Review</div>
          </div>
          <div className="aod-card">
            <div className="aod-card__top"><div className="aod-card__ic aod-ic-green">{Ico.check}</div></div>
            <div className="aod-card__num">
              {requests.reduce((n, r) => n + (r.diffs ? r.diffs.length : 0), 0)}
            </div>
            <div className="aod-card__lbl">Field Edits</div>
          </div>
        </div>

        <div className="aod-toolbar">
          <span className="crx-title">Profile Edit Requests</span>
          <span className="aod-count">Showing {filtered.length} of {requests.length} requests</span>
        </div>

        {error && (
          <div className="aod-error">{Ico.warn} <span>{error}</span><button onClick={load}>Retry</button></div>
        )}

        {loading && (
          <div className="aod-loading"><div className="aod-spinner" /><span>Loading change requests…</span></div>
        )}

        {!loading && !error && (
          filtered.length === 0 ? (
            <div className="aod-empty">
              <div className="aod-empty__ic">{Ico.inbox}</div>
              <h3>{q ? "No matching requests" : "No pending change requests"}</h3>
              <p>{q ? "Try a different name or email." : "All caught up! Employees haven't requested any edits."}</p>
            </div>
          ) : (
            <div className="crx-list">
              {filtered.map((r) => (
                <div key={r.id} className="crx-card">
                  <span className="aod-rail aod-rail--pending" />
                  <div className="crx-card__inner">
                    {/* Head */}
                    <div className="crx-head">
                      <div className="aod-who">
                        <span className={`aod-av ${avClass(r.email || r.fullName)}`}><span>{initials(r.fullName)}</span></span>
                        <div>
                          <b>{r.fullName}</b>
                          <span>{r.email}{r.employeeId ? ` · ${r.employeeId}` : ""}</span>
                        </div>
                      </div>
                      <span className="aod-sb aod-sb--pending"><i />Pending</span>
                    </div>

                    {r.reason && <p className="crx-reason"><span>Reason</span>{r.reason}</p>}

                    {/* Field diffs */}
                    {r.diffs && r.diffs.length > 0 && (
                      <div className="crx-diffs">
                        {r.diffs.map((d, i) => (
                          <div key={i} className="crx-diff">
                            <span className="crx-diff__label">{LABELS[d.field] || d.field}</span>
                            <div className="crx-diff__vals">
                              <span className="crx-old">{d.old || "—"}</span>
                              <span className="crx-arrow">{Ico.arrow}</span>
                              <span className="crx-new">{d.new}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Document previews */}
                    {r.documentPreviews && Object.keys(r.documentPreviews).length > 0 && (
                      <div className="crx-docs">
                        {Object.entries(r.documentPreviews).map(([key, url]) => (
                          <button key={key} className="crx-doc-btn" onClick={() => openPreview(LABELS[key] || key, url)}>
                            {Ico.doc} Preview new {LABELS[key] || key}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="crx-actions">
                      <button className="crx-btn crx-btn--approve" disabled={busyId === r.id} onClick={() => act(r.id, "APPROVE")}>
                        {busyId === r.id ? "Working…" : <>{Ico.check} Approve</>}
                      </button>
                      <button className="crx-btn crx-btn--reject" disabled={busyId === r.id} onClick={() => act(r.id, "REJECT")}>
                        {Ico.x} Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Preview modal */}
      {preview.open && (
        <div className="od-modal" onClick={closePreview}>
          <div className="od-modal__box" onClick={(e) => e.stopPropagation()}>
            <div className="od-modal__head">
              <h4>{preview.title}</h4>
              <button className="od-modal__close" onClick={closePreview}>×</button>
            </div>
            <div className="od-modal__body">
              {preview.isPdf
                ? <iframe title={preview.title} src={preview.url} className="od-modal__pdf" />
                : <img src={preview.url} alt={preview.title} className="od-modal__img" />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChangeRequests;
